/*
 * FractalAI bilingual rollout header.
 *
 * 文件定位 / File Purpose
 * - 中文：正式研究系统右侧上下文栏，展示当前页面摘要、相关入口和风险提示。
 * - English: Right-side context rail for the formal research system, showing page summaries, related entries, and risk reminders.
 *
 * 调用关系 / Call Flow
 * - 中文：由 app.ts/app.jsx 的研究外壳挂载；只读取路由元信息和导航配置。
 * - English: Mounted by the research shell in app.ts/app.jsx; reads only route metadata and navigation configuration.
 *
 * 数据与业务边界 / Data and Business Boundaries
 * - 中文：仅运行在浏览器侧或 Umi 构建侧，不直接访问数据库、不写 SQL、不复制服务端分型核心算法。
 * - English: Runs only in the browser or Umi build layer; it does not access databases, write SQL, or copy server-side fractal-core algorithms.
 *
 * 上线注意 / Release Notes
 * - 中文：作为智能研究工作台前端上线清单的一部分，修改时必须保持路由、白名单登录、报告/策略入口和生产构建校验一致。
 * - English: Part of the intelligence-workbench frontend rollout scope; keep routes, whitelist login, report/strategy entry points, and production-build checks aligned.
 */

import React, { useMemo } from 'react';
import { history } from '@umijs/max';
import { createStyles } from 'antd-style';
import useResearchDigest from '@/features/research/useResearchDigest';
import {
  buildReportCategoryPath,
  buildReportSlug,
  buildStrategySlug,
  findItemBySlug,
} from '@/utils/researchRouteIds';
import {
  AUTO_RESEARCH_LANE,
  resolveResearchRouteMeta,
} from '@/utils/researchNavigation';
import {
  buildResearchWorkspaceThemeStyle,
  isStrategySignalWorkspacePath,
  resolveResearchWorkspaceTheme,
} from '@/utils/researchWorkspaceTheme';

const useStyles = createStyles(() => ({
  wrapper: {
    position: 'relative',
    display: 'grid',
    gap: 6,
    width: '100%',
    maxHeight: 'none',
    overflowY: 'visible',
    paddingRight: 0,
    alignSelf: 'flex-start',
    textAlign: 'left',
    '@media (max-width: 1440px)': {
      display: 'none',
    },
  },
  panel: {
    display: 'grid',
    gap: 7,
    padding: '10px 11px',
    borderRadius: 8,
    border: '1px solid rgba(148, 163, 184, 0.16)',
    background:
      'var(--research-theme-rail-bg, linear-gradient(180deg, rgba(15, 23, 42, 0.96) 0%, rgba(15, 23, 42, 0.9) 58%, rgba(30, 64, 175, 0.74) 100%))',
    boxShadow: 'var(--research-theme-shadow, 0 10px 26px rgba(15, 23, 42, 0.12))',
    textAlign: 'left',
  },
  eyebrow: {
    color: 'var(--research-theme-eyebrow, #93c5fd)',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
  },
  title: {
    margin: 0,
    color: '#f8fafc',
    fontSize: 15,
    lineHeight: 1.3,
    fontWeight: 700,
  },
  desc: {
    margin: 0,
    color: 'rgba(226, 232, 240, 0.84)',
    fontSize: 12,
    lineHeight: 1.65,
  },
  section: {
    display: 'grid',
    gap: 8,
  },
  sectionTitle: {
    color: '#94a3b8',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },
  itemCard: {
    display: 'grid',
    gap: 4,
    padding: '8px 9px',
    borderRadius: 7,
    background: 'var(--research-theme-item-bg, rgba(255, 255, 255, 0.05))',
    border: '1px solid var(--research-theme-item-border, rgba(148, 163, 184, 0.12))',
    textAlign: 'left',
  },
  itemTitle: {
    color: '#f8fafc',
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1.5,
  },
  itemDesc: {
    color: 'rgba(226, 232, 240, 0.76)',
    fontSize: 11,
    lineHeight: 1.55,
  },
  actionButton: {
    justifySelf: 'start',
    padding: '5px 10px',
    borderRadius: 999,
    border: '1px solid var(--research-theme-action-border, rgba(125, 211, 252, 0.28))',
    background: 'var(--research-theme-action-bg, rgba(59, 130, 246, 0.14))',
    color: '#dbeafe',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'transform 180ms ease-out, background 180ms ease-out',
    ':hover': {
      transform: 'translateY(-1px)',
      background: 'var(--research-theme-action-hover-bg, rgba(59, 130, 246, 0.2))',
    },
  },
  pillRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
  },
  pill: {
    padding: '4px 8px',
    borderRadius: 999,
    border: '1px solid var(--research-theme-pill-border, rgba(148, 163, 184, 0.14))',
    background: 'var(--research-theme-pill-bg, rgba(148, 163, 184, 0.1))',
    color: '#e2e8f0',
    fontSize: 11,
    fontWeight: 600,
  },
  '@media (prefers-reduced-motion: reduce)': {
    actionButton: {
      transition: 'none',
    },
  },
}));

const SOURCE_LABELS = {
  market: '市场扫描',
  watchlist: '观察池联动',
  lightweight: '高性能图表工作台',
};

const COMPANY_ANALYSIS_PATH = '/company/analysis';
const HIDDEN_CONTEXT_RAIL_LANES = new Set([
  '/market/overview',
  '/research/symbol',
  AUTO_RESEARCH_LANE,
]);

const DASHBOARD_RAIL_LINKS = [
  {
    title: '全市场行情',
    description: '查看指数、板块、个股联动与市场结构。',
    path: '/market/overview',
  },
  {
    title: '个股研究',
    description: '从单标的进入结构研判和图表对照。',
    path: '/research/symbol',
  },
  {
    title: '策略与报告',
    description: '查看策略结果、报告资产和观察池回流。',
    path: '/strategy/library',
  },
  {
    title: '自动研究所',
    description: '进入研究所总屏，再选择每日复盘、公告新闻稿或公司事实与证据工作台。',
    path: AUTO_RESEARCH_LANE,
  },
];

const DASHBOARD_RAIL_RISKS = [
  {
    title: '页面边界',
    description: '驾驶舱只组织入口、读数和状态，不替代个股研判、策略复核或正式报告。',
  },
  {
    title: '数据边界',
    description: '读数来自当前研究资产快照，具体结论仍需进入对应工作区核对。',
  },
];

const DASHBOARD_RAIL_PILLS = ['驾驶舱总览', '主入口', '资产读数'];

const pickFirstValue = (target, keys) => {
  for (const key of keys) {
    if (target && target[key] !== undefined && target[key] !== null && target[key] !== '') {
      return target[key];
    }
  }
  return null;
};

const buildObjectSummary = (routeMeta, routeContext, currentObject) => {
  if (currentObject.report) {
    return `${currentObject.report.name || currentObject.report.filename || routeContext.reportId} · ${
      currentObject.report.category || '未分类'
    }`;
  }

  if (currentObject.strategy) {
    return `${currentObject.strategy.strategy || currentObject.strategy.name || routeContext.strategyId} · ${
      currentObject.strategy.resolution || '多周期'
    }`;
  }

  if (routeContext.compareLeft || routeContext.compareRight) {
    return `${routeContext.compareLeft || '左侧对象'} vs ${routeContext.compareRight || '右侧对象'}`;
  }

  if (routeContext.indexSymbol) {
    return `指数对象 ${routeContext.indexSymbol}`;
  }

  if (routeContext.symbol) {
    const sourceLabel = routeContext.source ? SOURCE_LABELS[routeContext.source] || routeContext.source : null;
    return sourceLabel
      ? `${routeContext.symbol} · 来自${sourceLabel}`
      : `当前标的 ${routeContext.symbol}`;
  }

  if (routeContext.groupId) {
    return `当前分组 ${routeContext.groupId}`;
  }

  return routeMeta.contextLabel || '当前页面未绑定具体对象，适合作为总览或工作台入口。';
};

const buildAssetSummary = ({ overviewLoading, partialError, overview }) => {
  if (overviewLoading) {
    return '正在同步观察池、策略结果和研究输出摘要。';
  }

  const baseText = `${overview.groupCount} 个观察组 · ${overview.strategyCount} 条策略结果 · ${overview.reportCount} 份研究输出`;
  return partialError ? `${baseText} · 当前为部分可用数据` : baseText;
};

const buildStatusSummary = ({
  trackedSymbol,
  statusLoading,
  statusError,
  latestStatus,
  statusSummary,
}) => {
  if (!trackedSymbol) {
    return '当前页面没有单一标的对象，右栏先展示研究工作区级摘要。';
  }

  if (statusLoading) {
    return `正在同步 ${trackedSymbol} 的分型预计算状态。`;
  }

  if (statusError) {
    return `${trackedSymbol} 的预计算状态暂未返回，当前仍可继续使用页面研究骨架。`;
  }

  const stateLabel = pickFirstValue(latestStatus, [
    'trendState',
    'trend_state',
    'status',
    'phase',
    'signal',
    'label',
  ]);
  const resolutionLabel = pickFirstValue(statusSummary, [
    'resolution',
    'currentResolution',
    'current_resolution',
  ]);
  const updatedLabel = pickFirstValue(statusSummary, [
    'updatedAt',
    'updated_at',
    'latestUpdate',
    'latest_update',
  ]) || pickFirstValue(latestStatus, ['updatedAt', 'updated_at']);
  const progressLabel =
    statusSummary && statusSummary.completed !== undefined && statusSummary.total !== undefined
      ? `${statusSummary.completed}/${statusSummary.total} 已完成`
      : null;

  const parts = [stateLabel, resolutionLabel, progressLabel, updatedLabel].filter(Boolean);
  if (parts.length > 0) {
    return parts.join(' · ');
  }

  return `${trackedSymbol} 已接入分型状态摘要。`;
};

const buildRelatedItems = (routeMeta, routeContext, currentObject) => {
  const currentSymbol = routeContext.symbol || routeContext.indexSymbol || routeContext.compareLeft;
  const items = [];

  if (routeMeta.activeLane === '/market/overview') {
    items.push({
      title: '打开市场信号中心',
      description: '继续从市场总览切到正式信号空间，查看板块异动与结构变化。',
      path: '/market/alerts',
    });
    items.push({
      title: '进入个股研究',
      description: currentSymbol
        ? `把 ${currentSymbol} 带入正式个股研究页继续拆解。`
        : '从市场工作区继续进入正式个股研究页。',
      path: currentSymbol ? `/research/symbol/${encodeURIComponent(currentSymbol)}` : '/research/symbol',
    });
    return items;
  }

  if (routeMeta.activeLane === '/research/symbol') {
    if (routeContext.symbol) {
      items.push({
        title: '切到对比研究',
        description: `把 ${routeContext.symbol} 带入对比研究，继续看相对强弱与对象差异。`,
        path: `/research/compare?left=${encodeURIComponent(routeContext.symbol)}&right=399001.SZ`,
      });
      items.push({
        title: '回到市场工作区',
        description: '回到全市场行情页确认当前个股所处的市场语境。',
        path: '/market/overview',
      });
      return items;
    }

    items.push({
      title: '打开研究观察池',
      description: '从对象研究切回观察池组织层，继续做分组跟踪和研究回流。',
      path: '/research/watchlists',
    });
    return items;
  }

  if (routeMeta.path === COMPANY_ANALYSIS_PATH) {
    items.push({
      title: '回到个股研究',
      description: currentSymbol
        ? `把 ${currentSymbol} 带入正式个股研究页，继续核对K线结构和策略信号。`
        : '回到个股研究页，核对K线结构、图表走势和策略信号。',
      path: currentSymbol ? `/research/symbol/${encodeURIComponent(currentSymbol)}` : '/research/symbol',
    });
    items.push({
      title: '返回研究所总屏',
      description: '回到 ResearchRun、证据、质量门和来源状态的统一总屏。',
      path: AUTO_RESEARCH_LANE,
    });
    return items;
  }

  if (routeMeta.activeLane === '/strategy/library') {
    if (routeMeta.path === '/reports/hotspot') {
      items.push({
        title: '打开归因报告',
        description: '从热点强弱继续看行业、主题、概念和新闻归因，确认热点背后的原因。',
        path: '/reports/attribution',
      });
      items.push({
        title: '回到策略与报告中心',
        description: '回到策略、报告和智能研究的主入口，继续切换其他工作区。',
        path: '/strategy/library',
      });
      return items;
    }

    if (routeMeta.path === '/reports/attribution') {
      items.push({
        title: '打开热点报告',
        description: '从归因拆解回看盘后热点强弱、拥挤程度和风险提示。',
        path: '/reports/hotspot',
      });
      items.push({
        title: '进入每日复盘',
        description: '把归因结论继续送入解释层，与复盘记录和对象研究合并查看。',
        path: '/intelligence/workbench',
      });
      return items;
    }

    if (currentObject.report) {
      items.push({
        title: '返回报告分类视图',
        description: '回到当前报告所属分类，继续做连续阅读和归档回看。',
        path: buildReportCategoryPath(currentObject.report.category || 'uncategorized'),
      });
      items.push({
        title: '进入解释中心',
        description: '把这份研究输出继续送入解释层和智能协同层。',
        path: '/intelligence/explainer',
      });
      return items;
    }

    if (currentObject.strategy) {
      items.push({
        title: '进入策略导入',
        description: '把当前策略结果送入正式导入链和观察池。',
        path: '/strategy/import',
      });
      items.push({
        title: '打开报告中心',
        description: '继续查看策略结果沉淀出的研究输出。',
        path: '/reports/list',
      });
      return items;
    }

    items.push({
      title: '打开策略信号中心',
      description: '查看正式策略信号空间里的候选结果与下游动作。',
      path: '/strategy/signals',
    });
    items.push({
      title: '打开报告归档',
      description: '切到研究输出的归档层做长期沉淀和分类回看。',
      path: '/reports/archive',
    });
    return items;
  }

  if (routeMeta.activeLane === AUTO_RESEARCH_LANE) {
    items.push({
      title: '打开解释中心',
      description: '查看报告说明、风险提示和模型解释，补充正式输出的阅读说明。',
      path: '/intelligence/explainer',
    });
    items.push({
      title: '回到个股研究',
      description: currentSymbol
        ? `把 ${currentSymbol} 带回个股研究页，继续核对K线结构和策略信号。`
        : '回到个股研究页，继续核对K线结构和策略信号。',
      path: currentSymbol ? `/research/symbol/${encodeURIComponent(currentSymbol)}` : '/research/symbol',
    });
    return items;
  }

  return items;
};

const buildRiskItems = (routeMeta, {
  partialError,
  trackedSymbol,
  statusError,
  currentObject,
}) => {
  if (routeMeta.path === COMPANY_ANALYSIS_PATH) {
    return [];
  }

  if (routeMeta.path === '/intelligence/workbench') {
    return [
      {
        title: '数据边界',
        description: '页面通过 BFF 调用 v_ReportMCP；分型、策略和报告数据以后台返回为准。',
      },
      {
        title: '复核边界',
        description: '正式报告用于业务参考前，仍需人工核对标的、周期、信号和关键结论。',
      },
    ];
  }

  if (routeMeta.path === '/reports/hotspot') {
    return [
      {
        title: '阅读边界',
        description: '热点报告用于观察盘后强弱和拥挤状态，不直接替代买卖依据。',
      },
      {
        title: '复核重点',
        description: '重点核对策略、周期、资产类型和报告日期，再进入归因或个股研究。',
      },
    ];
  }

  if (routeMeta.path === '/reports/attribution') {
    return [
      {
        title: '归因边界',
        description: '归因报告解释信号背后的行业、主题和新闻因素，不自动确认交易结论。',
      },
      {
        title: '复核重点',
        description: '重点核对归因链条、风险过滤和对应策略信号，再进入智能研究复盘。',
      },
    ];
  }

  const items = [
    {
      title: '当前阶段',
      description: routeMeta.modeLabel,
    },
  ];

  if (routeMeta.recommendedPath) {
    items.push({
      title: '推荐动作',
      description: routeMeta.recommendedLabel || '切换到推荐工作区',
      path: routeMeta.recommendedPath,
    });
  }

  if (statusError && trackedSymbol) {
    items.push({
      title: '预计算提示',
      description: `${trackedSymbol} 的分型状态暂未成功同步，当前请以工作区语义和旧执行链验证为准。`,
    });
  } else if (partialError) {
    items.push({
      title: '数据同步提示',
      description: '观察池或策略摘要当前为部分返回，右栏信息可能晚于主内容区。',
    });
  } else if (currentObject.report || currentObject.strategy) {
    items.push({
      title: '对象层状态',
      description: '当前详情页已经进入正式语义对象层，下一步可以继续接预览、下载或命中明细。',
    });
  }

  return items;
};

const ResearchContextRail = ({ location }) => {
  const { styles } = useStyles();
  const runtimeLocation = typeof window !== 'undefined' ? window.location : {};
  const pathname = location?.pathname || runtimeLocation.pathname || '';
  const search = location?.search || runtimeLocation.search || '';
  const routeMeta = resolveResearchRouteMeta(pathname, search);
  const workspaceTheme = resolveResearchWorkspaceTheme(pathname);
  const workspaceThemeStyle = buildResearchWorkspaceThemeStyle(workspaceTheme);
  const routeContext = routeMeta?.routeContext || {};
  const trackedSymbol = routeContext.symbol || routeContext.indexSymbol || null;
  const {
    overview,
    overviewLoading,
    partialError,
    reportItems,
    strategyItems,
    latestStatus,
    statusSummary,
    statusLoading,
    statusError,
  } = useResearchDigest({ symbol: trackedSymbol });

  const currentObject = useMemo(() => ({
    report: routeContext.reportId
      ? findItemBySlug(reportItems, routeContext.reportId, buildReportSlug)
      : null,
    strategy: routeContext.strategyId
      ? findItemBySlug(strategyItems, routeContext.strategyId, buildStrategySlug)
      : null,
  }), [reportItems, routeContext.reportId, routeContext.strategyId, strategyItems]);

  const isCompanyAnalysis = routeMeta?.path === COMPANY_ANALYSIS_PATH;
  const isDashboardWorkplace = routeMeta?.path === '/dashboard/workplace';
  const isIntelligenceWorkbench = routeMeta?.path === '/intelligence/workbench';
  const isHotspotReport = routeMeta?.path === '/reports/hotspot';
  const isAttributionReport = routeMeta?.path === '/reports/attribution';

  const summaryItems = useMemo(() => {
    if (isDashboardWorkplace) {
      return [
        {
          title: '入口拓扑',
          description: '首屏组织全市场、个股、策略与自动研究所四个入口，便于从总览快速进入工作区。',
        },
        {
          title: '资产读数',
          description: buildAssetSummary({ overviewLoading, partialError, overview }),
        },
        {
          title: '智能层',
          description: '展示 ReportMCP、宏观解释和共振评分等接入顺序，把准备状态放在驾驶舱视野内。',
        },
      ];
    }

    if (isCompanyAnalysis) {
      return [
        {
          title: '公告事实',
          description: '公告库提供原文入口；筛选只查库，同步近3日只补最新公告。',
        },
        {
          title: '证据与模型',
          description: '可靠信源核对事项、交易方和行业背景，模型只做推理与成稿辅助。',
        },
        {
          title: '草稿复核',
          description: '新闻稿记录保留证据和模型信息；重大数字、主体关系、监管表述需人工确认。',
        },
      ];
    }

    if (isIntelligenceWorkbench) {
      return [
        {
          title: '讨论窗口',
          description: '先做业务讨论和问题拆解，不生成正式报告，也不写入报告结果。',
        },
        {
          title: '现有分型智能',
          description: '进入现有分型智能，查询分型结构、策略6/7/8信号和相关研究数据。',
        },
        {
          title: '报告输出',
          description: '用户明确点击生成后，才输出 HTML/Markdown 分析报告并提供下载。',
        },
      ];
    }

    if (isHotspotReport) {
      return [
        {
          title: '热点强弱',
          description: '按策略、周期、资产类型和日期筛选，优先确认盘后热点强弱和扩散状态。',
        },
        {
          title: '拥挤与风险',
          description: '关注拥挤程度、风险提示和热点持续性，避免只看单份报告标题。',
        },
        {
          title: '后续归因',
          description: '热点结论需要继续切到归因报告，核对行业、主题、概念和新闻来源。',
        },
      ];
    }

    if (isAttributionReport) {
      return [
        {
          title: '归因拆解',
          description: '围绕策略信号查看行业、主题、概念、新闻和风险过滤等归因结果。',
        },
        {
          title: '策略范围',
          description: '当前归因阅读面向策略6、策略7和策略8，优先核对周期与资产类型。',
        },
        {
          title: '复盘接力',
          description: '重要归因结论可以继续进入每日复盘，与交易记录和对象研究合并判断。',
        },
      ];
    }

    return [
      {
        title: '当前阶段',
        description: routeMeta?.stageLabel,
      },
      {
        title: '当前对象',
        description: routeMeta ? buildObjectSummary(routeMeta, routeContext, currentObject) : '',
      },
      {
        title: '研究资产',
        description: buildAssetSummary({ overviewLoading, partialError, overview }),
      },
      {
        title: '分型状态',
        description: buildStatusSummary({
          trackedSymbol,
          statusLoading,
          statusError,
          latestStatus,
          statusSummary,
        }),
      },
    ];
  }, [
    currentObject,
    isAttributionReport,
    isDashboardWorkplace,
    isHotspotReport,
    isIntelligenceWorkbench,
    isCompanyAnalysis,
    latestStatus,
    overview,
    overviewLoading,
    partialError,
    routeContext,
    routeMeta,
    statusError,
    statusLoading,
    statusSummary,
    trackedSymbol,
  ]);

  let railCopy = {
    eyebrow: 'Context Rail',
    title: '当前页摘要',
    summaryTitle: '研究摘要',
    relatedTitle: '相关对象',
    riskTitle: '风险与提示',
  };

  if (isDashboardWorkplace) {
    railCopy = {
      eyebrow: 'Cockpit Rail',
      title: '智能驾驶舱',
      summaryTitle: '驾驶舱读数',
      relatedTitle: '主入口',
      riskTitle: '运行提示',
    };
  } else if (isCompanyAnalysis) {
    railCopy = {
      eyebrow: 'Disclosure Rail',
      title: '公告新闻稿摘要',
      summaryTitle: '工作流摘要',
      relatedTitle: '下游入口',
    };
  } else if (isIntelligenceWorkbench) {
    railCopy = {
      eyebrow: 'Intelligence Rail',
      title: '智能研究工作台',
      summaryTitle: '工作流摘要',
      relatedTitle: '下游入口',
      riskTitle: '运行边界',
    };
  } else if (isHotspotReport) {
    railCopy = {
      eyebrow: 'Hotspot Rail',
      title: '热点报告摘要',
      summaryTitle: '阅读重点',
      relatedTitle: '后续入口',
      riskTitle: '复核提示',
    };
  } else if (isAttributionReport) {
    railCopy = {
      eyebrow: 'Attribution Rail',
      title: '归因报告摘要',
      summaryTitle: '阅读重点',
      relatedTitle: '后续入口',
      riskTitle: '复核提示',
    };
  }

  const linkItems = useMemo(
    () => {
      if (isDashboardWorkplace) {
        return DASHBOARD_RAIL_LINKS;
      }
      return routeMeta ? buildRelatedItems(routeMeta, routeContext, currentObject) : [];
    },
    [currentObject, isDashboardWorkplace, routeContext, routeMeta],
  );

  const riskItems = useMemo(
    () => {
      if (isDashboardWorkplace) {
        return DASHBOARD_RAIL_RISKS;
      }
      return routeMeta
        ? buildRiskItems(routeMeta, {
            partialError,
            trackedSymbol,
            statusError,
            currentObject,
          })
        : [];
    },
    [currentObject, isDashboardWorkplace, partialError, routeMeta, statusError, trackedSymbol],
  );

  if (!routeMeta || HIDDEN_CONTEXT_RAIL_LANES.has(routeMeta.activeLane) || isStrategySignalWorkspacePath(pathname)) {
    return null;
  }

  const railDescription = isDashboardWorkplace
    ? '把研究入口、资产读数、方法底座和智能层状态压缩成一个总控视图。'
    : routeMeta.description;
  const railPills = isDashboardWorkplace
    ? DASHBOARD_RAIL_PILLS
    : [routeMeta.sectionLabel, routeMeta.modeLabel, routeMeta.contextLabel].filter(Boolean);

  return (
    <aside className={styles.wrapper} style={workspaceThemeStyle} aria-label="右侧解释栏">
      <section className={styles.panel}>
        <span className={styles.eyebrow}>{railCopy.eyebrow}</span>
        <h3 className={styles.title}>{railCopy.title}</h3>
        <p className={styles.desc}>{railDescription}</p>
        <div className={styles.pillRow}>
          {railPills.map(item => (
            <span key={item} className={styles.pill}>{item}</span>
          ))}
        </div>
      </section>

      <section className={styles.panel}>
        <span className={styles.sectionTitle}>{railCopy.summaryTitle}</span>
        <div className={styles.section}>
          {summaryItems.map((item) => (
            <div key={item.title} className={styles.itemCard}>
              <div className={styles.itemTitle}>{item.title}</div>
              <div className={styles.itemDesc}>{item.description}</div>
            </div>
          ))}
        </div>
      </section>

      <section className={styles.panel}>
        <span className={styles.sectionTitle}>{railCopy.relatedTitle}</span>
        <div className={styles.section}>
          {linkItems.length > 0 ? linkItems.map((item) => (
            <div key={item.title} className={styles.itemCard}>
              <div className={styles.itemTitle}>{item.title}</div>
              <div className={styles.itemDesc}>{item.description}</div>
              {item.path ? (
                <button
                  type="button"
                  className={styles.actionButton}
                  onClick={() => history.push(item.path)}
                >
                  打开
                </button>
              ) : null}
            </div>
          )) : (
            <div className={styles.itemCard}>
              <div className={styles.itemTitle}>待补更多联动对象</div>
              <div className={styles.itemDesc}>
                这一栏会继续承接 MCP 解释、相关报告和关联研究对象跳转。
              </div>
            </div>
          )}
        </div>
      </section>

      {riskItems.length > 0 ? (
        <section className={styles.panel}>
          <span className={styles.sectionTitle}>{railCopy.riskTitle}</span>
          <div className={styles.section}>
            {riskItems.map((item) => (
              <div key={item.title} className={styles.itemCard}>
                <div className={styles.itemTitle}>{item.title}</div>
                <div className={styles.itemDesc}>{item.description}</div>
                {item.path ? (
                  <button
                    type="button"
                    className={styles.actionButton}
                    onClick={() => history.push(item.path)}
                  >
                    打开
                  </button>
                ) : null}
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </aside>
  );
};

export default ResearchContextRail;
